import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

interface SalesPurchaseCumulativeProps {
  heading: string;
  rowData: any[];
  yr1Label?: string;
  yr2Label?: string;
}

const FY_ORDER = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
const MONTH_LABEL: Record<number, string> = {
  4: "Apr", 5: "May", 6: "Jun", 7: "Jul", 8: "Aug", 9: "Sep",
  10: "Oct", 11: "Nov", 12: "Dec", 1: "Jan", 2: "Feb", 3: "Mar",
};

const formatAmount = (val: number) =>
  Number(val || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const SalesPurchaseCumulative: React.FC<SalesPurchaseCumulativeProps> = ({
  heading,
  rowData,
  yr1Label: yr1Prop,
  yr2Label: yr2Prop,
}) => {
  const yr1Label = yr1Prop || "";
  const yr2Label = yr2Prop || "";

  const transformedData = useMemo(() => {
    // New format: array of { Particulars, CYData, LYData }
    if (Array.isArray(rowData) && rowData.length > 0 && rowData[0]?.LYData !== undefined) {
      const branches = rowData;

      const cyMaps: Map<number, number>[] = branches.map((b) => {
        const map = new Map<number, number>();
        (b.CYData || []).forEach((d: any) => map.set(d.Month, d.CumulativeCY));
        return map;
      });
      const lyMaps: Map<number, number>[] = branches.map((b) => {
        const map = new Map<number, number>();
        (b.LYData || []).forEach((d: any) => map.set(d.Month, d.CumulativeLY));
        return map;
      });

      const lyMonths = new Set<number>();
      branches.forEach((b) => (b.LYData || []).forEach((d: any) => lyMonths.add(d.Month)));
      const months = FY_ORDER.filter((m) => lyMonths.has(m));

      const carryForward = (map: Map<number, number>, month: number): number | null => {
        const idx = FY_ORDER.indexOf(month);
        for (let i = idx; i >= 0; i--) {
          if (map.has(FY_ORDER[i])) return map.get(FY_ORDER[i])!;
        }
        return null;
      };

      // Stop CY line at the last month any branch has CY data
      const lastCYFYIdx = cyMaps.reduce((max, cyMap) => {
        if (cyMap.size === 0) return max;
        const branchLast = Math.max(...Array.from(cyMap.keys()).map((m) => FY_ORDER.indexOf(m)));
        return Math.max(max, branchLast);
      }, -1);

      return months.map((month) => {
        const monthFYIdx = FY_ORDER.indexOf(month);
        let totalCY = 0, hasCY = false;
        if (monthFYIdx <= lastCYFYIdx) {
          cyMaps.forEach((m) => {
            const v = carryForward(m, month);
            if (v !== null) { totalCY += v; hasCY = true; }
          });
        }

        let totalLY = 0;
        lyMaps.forEach((m) => {
          const v = carryForward(m, month);
          if (v !== null) totalLY += v;
        });

        const growthPer =
          hasCY && totalLY > 0
            ? parseFloat((((totalCY - totalLY) / totalLY) * 100).toFixed(2))
            : null;

        return {
          Particulars: MONTH_LABEL[month],
          CumulativeCY: hasCY ? totalCY : null,
          CumulativeLY: totalLY,
          CGrowthPer: growthPer,
        };
      });
    }

    // Old format: { DataCY, DataLY }
    if (!(rowData as any)?.DataCY?.length) return [];
    const cyArr: any[] = (rowData as any).DataCY ?? [];
    const lyArr: any[] = (rowData as any).DataLY ?? [];
    return lyArr.map((ly: any, index: number) => {
      const cy = cyArr[index];
      const cumCY = cy?.CumulativeCY ?? null;
      const cumLY = ly.CumulativeLY ?? 0;
      const growthPer =
        cumCY !== null && cumLY > 0 && index >= 6
          ? parseFloat((((cumCY - cumLY) / cumLY) * 100).toFixed(2))
          : null;
      return {
        Particulars: ly.Particulars,
        CumulativeCY: cumCY,
        CumulativeLY: cumLY,
        CGrowthPer: growthPer,
      };
    });
  }, [rowData]);

  const maxYValue = useMemo(() => {
    if (!transformedData.length) return 0;
    const all = transformedData.flatMap((d) => [d.CumulativeCY ?? 0, d.CumulativeLY ?? 0]);
    return Math.max(...all);
  }, [transformedData]);

  const yAxisTicks = useMemo(() => {
    if (!maxYValue) return [];
    const step = maxYValue / 5;
    return [0, 1, 2, 3, 4, 5].map((i) => Math.round(step * i));
  }, [maxYValue]);

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "14px",
        border: "1px solid #dbe3ef",
        overflow: "hidden",
        boxShadow: "0 2px 12px rgba(15,23,42,0.09)",
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "13px 16px",
          borderBottom: "1px solid #e2e8f0",
          background: "#f8fafc",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "15px",
            fontWeight: 700,
            color: "#0f172a",
          }}
        >
          {heading} Cumulative Comparison
        </h3>
      </div>

      {/* Chart */}
      <div
        style={{
          height: "520px",
          padding: "24px 20px 16px",
          background: "#f8fafc",
          borderRadius: "0 0 14px 14px",
        }}
      >
        {!transformedData.length ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#94a3b8",
              fontSize: "14px",
            }}
          >
            No cumulative data available
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={transformedData}
              margin={{ top: 10, right: 60, left: 20, bottom: 80 }}
            >
              <CartesianGrid strokeDasharray="4 4" stroke="#e2e8f0" vertical={false} />

              <XAxis
                dataKey="Particulars"
                angle={-45}
                textAnchor="end"
                interval={0}
                height={80}
                tick={{ fontSize: 11, fill: "#64748b" }}
                axisLine={{ stroke: "#e2e8f0" }}
                tickLine={false}
              />

              <YAxis
                yAxisId="cy"
                domain={[0, maxYValue]}
                ticks={yAxisTicks}
                tick={{ fontSize: 11, fill: "#64748b" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) =>
                  v >= 10000000
                    ? `${(v / 10000000).toFixed(1)}Cr`
                    : v >= 100000
                    ? `${(v / 100000).toFixed(1)}L`
                    : v.toLocaleString("en-IN")
                }
              />

              <YAxis
                yAxisId="growth"
                orientation="right"
                tick={{ fontSize: 11, fill: "#f97316" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${v.toFixed(1)}%`}
              />

              <Tooltip
                content={({ active, payload, label }: any) => {
                  if (!active || !payload?.length) return null;
                  const cyVal =
                    payload.find((p: any) => p.dataKey === "CumulativeCY")?.value ?? null;
                  const lyVal =
                    payload.find((p: any) => p.dataKey === "CumulativeLY")?.value ?? 0;
                  const growthPer =
                    cyVal !== null && lyVal > 0
                      ? ((cyVal - lyVal) / lyVal) * 100
                      : null;
                  const growthColor =
                    growthPer === null
                      ? "#94a3b8"
                      : growthPer > 0
                      ? "#059669"
                      : "#ef4444";

                  return (
                    <div
                      style={{
                        background: "#ffffff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "10px",
                        padding: "10px 14px",
                        boxShadow: "0 4px 16px rgba(15,23,42,0.12)",
                        fontSize: "13px",
                        minWidth: "200px",
                      }}
                    >
                      <p
                        style={{
                          margin: "0 0 8px",
                          fontWeight: 700,
                          color: "#0f172a",
                          borderBottom: "1px solid #e2e8f0",
                          paddingBottom: "6px",
                        }}
                      >
                        {label}
                      </p>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          margin: "4px 0",
                        }}
                      >
                        <span style={{ color: "#3b82f6", fontWeight: 600 }}>
                          FY {yr1Label}
                        </span>
                        <span style={{ fontWeight: 700, color: "#0f172a" }}>
                          {formatAmount(lyVal)}
                        </span>
                      </div>
                      {cyVal !== null && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            margin: "4px 0",
                          }}
                        >
                          <span style={{ color: "#1e3a5f", fontWeight: 600 }}>
                            FY {yr2Label}
                          </span>
                          <span style={{ fontWeight: 700, color: "#0f172a" }}>
                            {formatAmount(cyVal)}
                          </span>
                        </div>
                      )}
                      {growthPer !== null && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            margin: "6px 0 0",
                            paddingTop: "6px",
                            borderTop: "1px solid #e2e8f0",
                          }}
                        >
                          <span style={{ color: "#64748b", fontWeight: 600 }}>
                            Growth %
                          </span>
                          <span style={{ fontWeight: 700, color: growthColor }}>
                            {growthPer > 0 ? "▲ " : "▼ "}
                            {Math.abs(growthPer).toFixed(2)}%
                          </span>
                        </div>
                      )}
                    </div>
                  );
                }}
              />

              <Legend
                wrapperStyle={{ paddingTop: "12px", fontSize: "12px" }}
                iconType="circle"
              />

              <Line
                yAxisId="cy"
                type="monotone"
                dataKey="CumulativeCY"
                name={`FY ${yr2Label}`}
                stroke="#1e3a5f"
                strokeWidth={3}
                dot={false}
                connectNulls={false}
                activeDot={{ r: 5, fill: "#1e3a5f", stroke: "#fff", strokeWidth: 2 }}
              />

              <Line
                yAxisId="cy"
                type="monotone"
                dataKey="CumulativeLY"
                name={`FY ${yr1Label}`}
                stroke="#3b82f6"
                strokeWidth={3}
                dot={false}
                connectNulls={false}
                activeDot={{ r: 5, fill: "#3b82f6", stroke: "#fff", strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default SalesPurchaseCumulative;
